import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Plus, Trash, X, CaretDown, MagnifyingGlass } from '@phosphor-icons/react';

/**
 * Top-right "My Loadmen (N)" pill button on the pilot dashboard.
 * Tapping opens a popup with halt picker + search + add/remove flow.
 * Below the button (outside this component) the parent shows the short names.
 *
 * onChange(list) fires whenever the assigned list is mutated so the parent can
 * render its summary line of chips.
 */
export default function PilotLoadmanButton({ assigned = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [haltOptions, setHaltOptions] = useState([]);
  const [halt, setHalt] = useState('');
  const [haltSearch, setHaltSearch] = useState('');
  const [showHaltList, setShowHaltList] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHalts = useCallback(async () => {
    try {
      const { data } = await api.get('/pilot/halt-locations');
      setHaltOptions(Array.isArray(data) ? data : []);
    } catch {
      setHaltOptions([]);
    }
  }, []);

  const fetchCandidates = useCallback(async () => {
    if (!halt) { setCandidates([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/pilot/loadmen', {
        params: { halt_location: halt, q: query || undefined },
      });
      setCandidates(Array.isArray(data) ? data : []);
    } catch {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [halt, query]);

  useEffect(() => { if (open) fetchHalts(); }, [open, fetchHalts]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(fetchCandidates, 200);
    return () => clearTimeout(t);
  }, [open, fetchCandidates]);

  const add = async (loadmanId) => {
    try {
      const { data } = await api.post('/pilot/my-loadmen', { loadman_id: loadmanId });
      onChange?.(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add loadman');
    }
  };

  const remove = async (loadmanId) => {
    try {
      const { data } = await api.delete(`/pilot/my-loadmen/${loadmanId}`);
      onChange?.(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove loadman');
    }
  };

  const assignedIds = new Set(assigned.map(a => a.loadman_id));
  const notYetAssigned = candidates.filter(c => !assignedIds.has(c.id));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#002FA7] text-white text-xs uppercase tracking-widest hover:bg-[#002FA7]/90"
        data-testid="my-loadmen-btn"
      >
        <Users size={14} weight="bold" />
        My Helpers ({assigned.length})
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="my-loadmen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={18} className="text-[#002FA7]" /> My Helpers
              <span className="text-xs font-normal text-[#666]">({assigned.length})</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Assigned list */}
            {assigned.length > 0 && (
              <div className="space-y-1.5">
                {assigned.map(a => (
                  <div
                    key={a.loadman_id}
                    className="flex items-center justify-between gap-3 px-3 py-2 bg-[#F7F7F7] border border-[#E5E5E5]"
                    data-testid={`assigned-loadman-${a.loadman_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0A0A0A] truncate">{a.name}</p>
                      <p className="text-[10px] text-[#666] font-mono truncate">
                        {a.emp_id || '—'}
                        {a.halt_location ? ` · ${a.halt_location}` : ''}
                        {a.type ? ` · ${a.type === 'contract' ? 'Contract' : 'Own'}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(a.loadman_id)}
                      className="p-1.5 text-[#FF3333] hover:bg-[#FF3333]/10"
                      title="Remove"
                      data-testid={`remove-loadman-${a.loadman_id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Picker */}
            <div className="border-t border-[#E5E5E5] pt-3 space-y-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#666]">1 · Halt Location</label>
                <button
                  type="button"
                  onClick={() => setShowHaltList(v => !v)}
                  data-testid="pilot-loadman-halt"
                  className="flex items-center justify-between w-full h-10 px-3 text-sm bg-white border border-[#E5E5E5] hover:border-[#0A0A0A]"
                >
                  <span className={halt ? 'text-[#0A0A0A]' : 'text-[#999]'}>
                    {halt || 'Pick halt to list helpers'}
                  </span>
                  <CaretDown size={14} className="text-[#666]" />
                </button>
                {showHaltList && (
                  <div className="mt-2 border border-[#E5E5E5] bg-white">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-[#F3F3F3]">
                      <MagnifyingGlass size={14} className="text-[#666]" />
                      <input
                        type="text"
                        value={haltSearch}
                        onChange={e => setHaltSearch(e.target.value)}
                        placeholder="Search halt..."
                        className="flex-1 text-sm outline-none bg-transparent"
                        data-testid="pilot-loadman-halt-search"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {haltOptions
                        .filter(h => !haltSearch || h.toLowerCase().includes(haltSearch.toLowerCase()))
                        .map(h => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => { setHalt(h); setShowHaltList(false); setHaltSearch(''); }}
                            data-testid={`pilot-loadman-halt-opt-${h}`}
                            className={`w-full text-left text-sm px-3 py-2 border-b border-[#F3F3F3] last:border-b-0 hover:bg-[#002FA7]/5 ${halt === h ? 'bg-[#002FA7]/10 font-medium text-[#002FA7]' : 'text-[#0A0A0A]'}`}
                          >
                            {h}
                          </button>
                        ))}
                      {haltOptions.filter(h => !haltSearch || h.toLowerCase().includes(haltSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-[#666] p-3">No halt matches</p>
                      )}
                    </div>
                    {halt && (
                      <button
                        type="button"
                        onClick={() => { setHalt(''); setShowHaltList(false); }}
                        className="w-full text-xs uppercase tracking-widest text-[#666] py-2 border-t border-[#F3F3F3] hover:bg-[#F3F3F3]"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>

              {halt && (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#666]">2 · Name or Emp / Contract ID</label>
                    <input
                      type="text"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="e.g. Ram or L-101"
                      className="w-full border border-[#E5E5E5] px-3 py-2 text-sm rounded-full focus:outline-none focus:border-[#002FA7]"
                      data-testid="pilot-loadman-search"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-[#F3F3F3]" data-testid="pilot-loadman-candidates">
                    {loading ? (
                      <p className="text-xs text-[#666] p-3">Loading...</p>
                    ) : notYetAssigned.length === 0 ? (
                      <p className="text-xs text-[#666] p-3">
                        {candidates.length === 0
                          ? 'No loadmen at this halt.'
                          : 'All matching loadmen already added.'}
                      </p>
                    ) : (
                      notYetAssigned.map(c => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#F3F3F3] last:border-b-0"
                          data-testid={`candidate-loadman-${c.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0A0A0A] truncate">{c.name}</p>
                            <p className="text-[10px] text-[#666] font-mono truncate">
                              {c.emp_id || '—'}
                              {c.type ? ` · ${c.type === 'contract' ? 'Contract' : 'Own'}` : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => add(c.id)}
                            className="p-1.5 bg-[#002FA7] text-white hover:bg-[#002FA7]/90 flex items-center gap-1 text-xs uppercase tracking-widest"
                            data-testid={`add-loadman-${c.id}`}
                          >
                            <Plus size={12} weight="bold" /> Add
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end pt-2 border-t border-[#E5E5E5]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-widest hover:bg-[#0A0A0A]/90 flex items-center gap-1"
                data-testid="my-loadmen-close-btn"
              >
                <X size={12} weight="bold" /> Done
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
